import { Box, CircularProgress, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useDealImages } from "@/hooks/useDealImages";
import type { DealImage } from "@/types/dealImages";

type DealPhotosPanelProps = { dealId: number };

const linkSx = {
  fontSize: 12,
  fontFamily: '"Helvetica Neue", sans-serif',
  color: "#1B7A63",
  textDecoration: "none",
  "&:hover": { textDecoration: "underline" },
} as const;

function Photo({
  image,
  aspectRatio,
  showCaption,
}: {
  image: DealImage;
  aspectRatio: string;
  showCaption: boolean;
}) {
  return (
    <Box
      component="a"
      href={image.url}
      target="_blank"
      rel="noopener noreferrer"
      title={image.caption ?? "Open full size"}
      sx={{ display: "block", textDecoration: "none", minWidth: 0 }}
    >
      <Box
        component="img"
        src={image.url}
        alt={image.caption ?? "Deal photo"}
        loading="lazy"
        sx={{
          display: "block",
          width: "100%",
          aspectRatio,
          objectFit: "cover",
          borderRadius: "8px",
          backgroundColor: "#e4e7ec",
          transition: "opacity 120ms",
          "&:hover": { opacity: 0.9 },
        }}
      />
      {showCaption && image.caption && (
        <Typography noWrap sx={{ mt: 0.5, fontSize: 12, color: "#475467" }}>
          {image.caption}
        </Typography>
      )}
    </Box>
  );
}

/**
 * The deal's photos in the side panel: the primary photo large, the rest as
 * thumbnails. Click a photo to open it full size; managing photos stays on
 * the deal page's Photos tab.
 */
export function DealPhotosPanel({ dealId }: DealPhotosPanelProps) {
  const { hero, images, loading, error } = useDealImages(dealId);
  const others = hero ? images.filter((img) => img.id !== hero.id) : images;
  const primary = hero ?? others[0] ?? null;
  const thumbnails = hero ? others : others.slice(1);
  const manageHref = `/deals/${dealId}?tab=gallery`;

  if (loading && !primary) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error && !primary) {
    return (
      <Typography color="error" variant="body2" sx={{ p: 2 }}>
        {error}
      </Typography>
    );
  }

  if (!primary) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography sx={{ fontSize: 13, color: "#475467", mb: 1 }}>
          No photos for this deal yet.
        </Typography>
        <Box component={RouterLink} to={manageHref} sx={linkSx}>
          Add photos on the deal page
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Photo image={primary} aspectRatio="16 / 10" showCaption />
      {thumbnails.length > 0 && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: 1,
          }}
        >
          {thumbnails.map((image) => (
            <Photo
              key={image.id}
              image={image}
              aspectRatio="4 / 3"
              showCaption={false}
            />
          ))}
        </Box>
      )}
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: 12, color: "#667085" }}>
          {images.length} photo{images.length === 1 ? "" : "s"}
        </Typography>
        <Box component={RouterLink} to={manageHref} sx={linkSx}>
          Manage photos
        </Box>
      </Box>
    </Box>
  );
}
